import { Server, TriangleAlert } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { CardLink } from "../dashboard/DashboardPrimitives";
import { formatAge } from "../gpu-fleet/fleetSummary";
import {
  GPU_FAMILY_LABELS,
  type GpuFamilyFleet,
  type H3Fleet,
  type SelfHostedWorker,
  describeWorkerState,
} from "../gpu-fleet/h3Fleet";

const STATE_VARIANT: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  idle: "default",
  busy: "secondary",
  draining: "outline",
  blocked_gpu_busy: "outline",
  quarantined: "destructive",
  stale: "destructive",
  offline: "outline",
};

function runpodSummary(runpod: NonNullable<H3Fleet["runpod"]>): string {
  const w = runpod.workers;
  const parts = [
    `${w.idle ?? 0} idle`,
    `${w.ready ?? 0} ready`,
    `${w.running ?? 0} running`,
    `${w.initializing ?? 0} booting`,
  ];
  if (w.throttled) parts.push(`${w.throttled} throttled`);
  return parts.join(" · ");
}

function WorkerTable({ workers }: { workers: SelfHostedWorker[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Worker</TableHead>
          <TableHead>State</TableHead>
          <TableHead>GPU</TableHead>
          <TableHead>Job</TableHead>
          <TableHead>Clips</TableHead>
          <TableHead>Image</TableHead>
          <TableHead>Last seen</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {workers.map((worker) => (
          <TableRow key={worker.worker_id}>
            <TableCell className="font-mono text-xs">
              {worker.worker_id}
              {worker.location ? (
                <span className="block text-muted-foreground">
                  {worker.location}
                </span>
              ) : null}
            </TableCell>
            <TableCell>
              <Badge
                variant={STATE_VARIANT[worker.state] ?? "outline"}
                title={describeWorkerState(worker.state)}
              >
                {worker.state}
              </Badge>
            </TableCell>
            <TableCell className="text-xs">
              {worker.gpu ?? "—"}
              {worker.vram_gib ? (
                <span className="block text-muted-foreground">
                  {worker.vram_gib} GiB · {worker.profile ?? "?"}
                </span>
              ) : null}
            </TableCell>
            <TableCell className="font-mono text-xs">
              {worker.current_job_id ? worker.current_job_id.slice(0, 8) : "—"}
            </TableCell>
            <TableCell className="text-xs">
              {worker.jobs_completed ?? 0} ok
              {worker.jobs_failed ? ` · ${worker.jobs_failed} failed` : ""}
            </TableCell>
            <TableCell
              className="font-mono text-xs"
              title={worker.image_digest ?? undefined}
            >
              {worker.image_digest ? worker.image_digest.slice(7, 19) : "—"}
            </TableCell>
            <TableCell className="text-xs">
              {formatAge(worker.last_seen_seconds_ago)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// The H3 GPU fleet: self-hosted workers pulling from the pool queue (one row per
// machine, from its registry heartbeat) plus the RunPod endpoint's worker counts.
// With /config/gpu-fleet, every other model family (LTX-2.5 for real estate)
// follows in its own card.
export function FleetCard({ fleet }: { fleet: H3Fleet }) {
  const others = Object.entries(fleet.families ?? {})
    .filter(([id]) => id !== "h3")
    .sort(([left], [right]) =>
      left === "ltx25" ? -1 : right === "ltx25" ? 1 : left.localeCompare(right),
    );
  return (
    <>
      <H3FleetCard fleet={fleet} />
      {others.map(([id, family]) => (
        <FamilyFleetCard key={id} familyId={id} family={family} />
      ))}
    </>
  );
}

function H3FleetCard({ fleet }: { fleet: H3Fleet }) {
  const { self_hosted: selfHosted, runpod } = fleet;
  const quarantined = selfHosted.counts.quarantined ?? 0;
  const stale = selfHosted.counts.stale ?? 0;
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Server aria-hidden className="size-4 text-muted-foreground" />
          <CardTitle className="text-base">H3 GPU fleet</CardTitle>
          <Badge
            variant="default"
            title="Idle self-hosted workers a clip can be handed to right now"
          >
            {selfHosted.capacity} free self-hosted
          </Badge>
          {runpod ? (
            <Badge
              variant="outline"
              className="font-mono text-xs font-normal text-muted-foreground"
              title={`RunPod endpoint ${runpod.endpoint_id}: queue ${runpod.jobs.inQueue ?? 0}, in progress ${runpod.jobs.inProgress ?? 0}`}
            >
              RunPod · {runpodSummary(runpod)}
            </Badge>
          ) : null}
          {quarantined > 0 ? (
            <Badge
              variant="destructive"
              title="A worker failed five clips in a row and stopped taking jobs; clear it on the box"
            >
              <TriangleAlert aria-hidden className="size-3" /> {quarantined}{" "}
              quarantined
            </Badge>
          ) : null}
          {stale > 0 ? (
            <Badge
              variant="destructive"
              title="No heartbeat for more than 90 s"
            >
              <TriangleAlert aria-hidden className="size-3" /> {stale} silent
            </Badge>
          ) : null}
          <span className="ml-auto">
            <CardLink to="/gpu-fleet">Open GPU Fleet</CardLink>
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {selfHosted.workers.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No self-hosted worker has enrolled in this environment. Every H3
            clip renders on RunPod.
          </p>
        ) : (
          <WorkerTable workers={selfHosted.workers} />
        )}
        {fleet.notes.length > 0 ? (
          <ul className="list-disc pl-4 text-xs text-muted-foreground">
            {fleet.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Clips the family's supervisor has in flight and has finished in the live slice. */
function jobsSummary(jobs: GpuFamilyFleet["jobs"]): string | null {
  if (!jobs) return null;
  const planes = Object.values(jobs.by_plane ?? {});
  const completed = planes.reduce(
    (sum, plane) => sum + (plane.completed ?? 0),
    0,
  );
  const failed = planes.reduce((sum, plane) => sum + (plane.failed ?? 0), 0);
  const parts = [
    `${jobs.in_flight?.length ?? 0} in flight`,
    `${completed} done`,
  ];
  if (failed) parts.push(`${failed} failed`);
  return typeof jobs.window_hours === "number"
    ? `${parts.join(" · ")} (last ${jobs.window_hours} h)`
    : parts.join(" · ");
}

// One non-H3 model family from /config/gpu-fleet, e.g. LTX-2.5 (`ltx25`), the
// real-estate lane: RunPod-only in Phase 1, so the worker table usually says
// nobody has enrolled.
function FamilyFleetCard({
  familyId,
  family,
}: {
  familyId: string;
  family: GpuFamilyFleet;
}) {
  const label = GPU_FAMILY_LABELS[familyId] ?? familyId;
  const workers = family.self_hosted?.workers ?? [];
  const jobs = jobsSummary(family.jobs);
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Server aria-hidden className="size-4 text-muted-foreground" />
          <CardTitle className="text-base">{label} GPU fleet</CardTitle>
          {familyId === "ltx25" ? (
            <Badge variant="outline">Real estate</Badge>
          ) : null}
          {family.self_hosted ? (
            <Badge
              variant="default"
              title="Idle self-hosted workers a clip can be handed to right now"
            >
              {family.self_hosted.capacity ?? 0} free self-hosted
            </Badge>
          ) : null}
          {family.runpod ? (
            <Badge
              variant="outline"
              className="font-mono text-xs font-normal text-muted-foreground"
              title={`RunPod endpoint ${family.runpod.endpoint_id}: queue ${family.runpod.jobs.inQueue ?? 0}, in progress ${family.runpod.jobs.inProgress ?? 0}`}
            >
              RunPod · {runpodSummary(family.runpod)}
            </Badge>
          ) : (
            <Badge variant="outline">No RunPod endpoint</Badge>
          )}
          {jobs ? (
            <span className="text-xs text-muted-foreground">{jobs}</span>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {workers.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No self-hosted {label} worker has enrolled in this environment.
            {family.runpod ? ` Every ${label} clip renders on RunPod.` : ""}
          </p>
        ) : (
          <WorkerTable workers={workers} />
        )}
        {family.notes.length > 0 ? (
          <ul className="list-disc pl-4 text-xs text-muted-foreground">
            {family.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        ) : null}
      </CardContent>
    </Card>
  );
}
